<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title class="text-h6">Quality Aspects by Category</v-card-title>
          <v-card-text>
            <canvas ref="categoryChartRef"></canvas>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title class="text-h6">Quality Aspects by Impact</v-card-title>
          <v-card-text>
            <canvas ref="impactChartRef"></canvas>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, computed, defineProps } from 'vue'
import Chart from 'chart.js/auto'
import segmentData from '@/data/segment.json'

const props = defineProps({
  qualityAspects: {
    type: Array,
    required: true
  }
})

// Build sunburst category color mapping from segment.json
const sunburstCategoryColors = {};
segmentData.forEach(cat => {
  sunburstCategoryColors[cat.text.en.name] = cat.color;
});

const chartColors = {
  categories: sunburstCategoryColors,
  // Use sunburst-inspired, semi-transparent RGBA for impact
  impacts: {
    'High': 'rgba(244, 67, 54, 0.6)',    // Soft red
    'Medium': 'rgba(255, 193, 7, 0.6)',  // Soft orange
    'Low': 'rgba(139, 195, 74, 0.6)'     // Soft green
  }
};

const getQualityAspectName = (id) => {
  const mapping = {
    '1_1': 'Code Quality',
    '1_2': 'Architecture Design',
    '1_3': 'Testing Coverage',
    '1_4': 'Performance Optimization',
    '1_5': 'Security Measures',
    '1_6': 'Documentation',
    '2_1': 'Business Value',
    '2_2': 'Market Fit',
    '2_3': 'User Experience',
    '2_4': 'Scalability',
    '3_1': 'Team Expertise',
    '3_2': 'Resource Allocation',
    '3_3': 'Project Timeline',
    '3_4': 'Communication',
    '4_1': 'Data Protection',
    '4_2': 'Access Control',
    '4_3': 'Vulnerability Management',
    '4_4': 'Incident Response',
    '5_1': 'Regulatory Compliance',
    '5_2': 'Industry Standards',
    '5_3': 'Legal Requirements',
    '5_4': 'Audit Readiness'
  };
  return mapping[id] || `Topic ${id}`;
};

const getCategoryColor = (category) => {
  return chartColors.categories[category] || '#000000';
};

const getCategoryLabel = (category) => {
  return category.length > 15 ? category.slice(0, 12) + '...' : category;
};

const aspectsByCategory = computed(() => {
  if (!props.qualityAspects) return {}
  
  return props.qualityAspects.reduce((acc, aspect) => {
    const category = aspect.category || 'Technical'
    if (!acc[category]) acc[category] = []
    acc[category].push({
      ...aspect,
      name: getQualityAspectName(aspect.id)
    })
    return acc
  }, {})
})

const aspectsByImpact = computed(() => {
  if (!props.qualityAspects) return {}
  
  return props.qualityAspects.reduce((acc, aspect) => {
    const impact = aspect.impact || 'Medium'
    if (!acc[impact]) acc[impact] = []
    acc[impact].push({
      ...aspect,
      name: getQualityAspectName(aspect.id)
    })
    return acc
  }, {})
})

const categoryChartData = computed(() => {
  const labels = Object.keys(aspectsByCategory.value)
  const data = labels.map(category => aspectsByCategory.value[category].length)
  const backgroundColor = labels.map(category => getCategoryColor(category))
  
  return {
    labels: labels.map(category => getCategoryLabel(category)),
    datasets: [{
      data,
      backgroundColor
    }]
  }
})

const impactChartData = computed(() => {
  const labels = Object.keys(aspectsByImpact.value)
  const data = labels.map(impact => aspectsByImpact.value[impact].length)
  const backgroundColor = labels.map(impact => chartColors.impacts[impact])
  
  return {
    labels: labels.map(impact => `${impact} (${aspectsByImpact.value[impact].length})`),
    datasets: [{
      data,
      backgroundColor
    }]
  }
})

const categoryChartRef = ref(null)
const impactChartRef = ref(null)
let categoryChart = null
let impactChart = null

const createCharts = () => {
  if (!categoryChartRef.value || !impactChartRef.value) return

  // Create category chart
  categoryChart = new Chart(categoryChartRef.value, {
    type: 'pie',
    data: categoryChartData.value,
    options: {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom'
        }
      }
    }
  })

  // Create impact chart
  impactChart = new Chart(impactChartRef.value, {
    type: 'pie',
    data: impactChartData.value,
    options: {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom'
        }
      }
    }
  })
}

const updateCharts = () => {
  // Destroy existing charts if they exist
  if (categoryChart) {
    categoryChart.destroy();
    categoryChart = null;
  }
  if (impactChart) {
    impactChart.destroy();
    impactChart = null;
  }

  if (!categoryChartRef.value || !impactChartRef.value) return;

  // Create category chart
  categoryChart = new Chart(categoryChartRef.value, {
    type: 'pie',
    data: categoryChartData.value,
    options: {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom'
        },
        title: {
          display: true,
          text: 'Quality Aspects by Category'
        }
      }
    }
  });

  // Create impact chart
  impactChart = new Chart(impactChartRef.value, {
    type: 'pie',
    data: impactChartData.value,
    options: {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom'
        },
        title: {
          display: true,
          text: 'Quality Aspects by Impact'
        }
      }
    }
  });
}

watch(() => props.qualityAspects, () => {
  updateCharts()
}, { deep: true })

onMounted(() => {
  createCharts()
})

onBeforeUnmount(() => {
  if (categoryChart) {
    categoryChart.destroy()
  }
  if (impactChart) {
    impactChart.destroy()
  }
})
</script>

<style scoped>
.v-card {
  height: 100%;
}

.v-card-text {
  height: 300px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.v-card-title {
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
  padding: 16px;
}

canvas {
  max-height: 100%;
  width: auto !important;
  max-width: 100%;
}

.v-list-item-subtitle {
  white-space: normal;
}

.quality-impact-section {
  height: auto !important;
  min-height: 200px;
}
</style> 